import React, { useState } from "react";
import {
  Heart,
  Utensils,
  Droplets,
  Minimize2,
  Maximize2,
} from "lucide-react";

export default function SurvivalHUD({ stats }) {
  const [isMinimized, setIsMinimized] = useState(
    () => localStorage.getItem("survivalHUDMinimized") === "true",
  );

  if (!stats) return null;

  const toggleMinimized = () => {
    setIsMinimized((currentValue) => {
      const nextValue = !currentValue;
      localStorage.setItem("survivalHUDMinimized", String(nextValue));
      return nextValue;
    });
  };

  const getWarningClass = (value, defaultClass) =>
    value < 25 ? "text-red-600 animate-pulse" : defaultClass;

  const StatBar = ({ value, color, icon: Icon, label }) => (
    <div className="flex flex-col items-center gap-2 group">
      <div className="relative w-2 h-20 bg-black border border-zinc-800 overflow-hidden rounded-full">
        <div
          className={`absolute bottom-0 w-full transition-all duration-1000 ease-out ${color} ${value < 25 ? "animate-pulse" : ""}`}
          style={{ height: `${value}%` }}
        />
      </div>
      <div className="flex flex-col items-center">
        <Icon
          size={12}
          className={
            value < 25 ? "text-red-600 animate-bounce" : "text-zinc-500"
          }
        />
        <span className="text-[7px] font-black text-zinc-600 uppercase mt-1 tracking-tighter">
          {label}
        </span>
      </div>
    </div>
  );

  if (isMinimized) {
    return (
      <button
        type="button"
        onClick={toggleMinimized}
        aria-expanded="false"
        className="hidden lg:flex absolute -top-[42px] right-8 h-10 items-center gap-3 bg-zinc-950 border-x-2 border-t-2 border-zinc-900 px-3 text-zinc-500 hover:text-white hover:border-zinc-700 transition-colors z-[10]"
        title="Show survival vitals"
      >
        <Heart
          size={13}
          className={getWarningClass(stats.health, "text-red-800")}
        />
        <Utensils
          size={13}
          className={getWarningClass(stats.hunger, "text-orange-700")}
        />
        <Droplets
          size={13}
          className={getWarningClass(stats.thirst, "text-blue-700")}
        />
        <span className="text-[8px] font-black uppercase tracking-widest">
          Vitals
        </span>
        <Maximize2 size={12} />
      </button>
    );
  }

  return (
    /* Absolute positioning tethered to the footer's top edge */
    <div className="hidden lg:flex absolute -top-[140px] right-8 bg-zinc-950 border-x-2 border-t-2 border-zinc-900 p-4 pb-2 gap-6 shadow-[-20px_-20px_40px_rgba(0,0,0,0.5)] z-[10]">
      <button
        type="button"
        onClick={toggleMinimized}
        aria-expanded="true"
        className="absolute top-2 right-2 p-1 text-zinc-600 hover:text-white transition-colors"
        title="Minimize survival vitals"
      >
        <Minimize2 size={12} />
      </button>

      <StatBar
        value={stats.health}
        color="bg-red-600"
        icon={Heart}
        label="VIT"
      />
      <StatBar
        value={stats.hunger}
        color="bg-orange-600"
        icon={Utensils}
        label="NUT"
      />
      <StatBar
        value={stats.thirst}
        color="bg-blue-600"
        icon={Droplets}
        label="HYD"
      />

      <div className="flex flex-col justify-center border-l border-zinc-900 pl-4 min-w-[70px]">
        <span className="text-[8px] font-black text-zinc-500 uppercase mb-1 tracking-widest">
          State
        </span>
        <span
          className={`text-[10px] font-black italic uppercase leading-none ${stats.state === "alive" ? "text-green-600" : "text-red-600 animate-pulse"}`}
        >
          {stats.state === "alive" ? "OPERATIONAL" : stats.state}
        </span>
      </div>

      {/* Bridge div to hide the footer's top border line under the HUD */}
      <div className="absolute -bottom-[2px] left-0 w-full h-[3px] bg-zinc-950"></div>
    </div>
  );
}
